import { z } from "zod";

import ChatImage from "@/components/ui/chat/image/ChatImage";

/**
 * Chat catalog definition for images. Use in renderer catalog.
 * Renders a responsive image with optional caption — suited for article
 * thumbnails, publisher logos, or brand assets inside chat bubbles.
 */
export const ChatImageSpec = {
  props: z.object({
    src: z.string(),
    alt: z.string(),
    caption: z.string().nullable().optional(),
    rounded: z.boolean().nullable().optional(),
    maxHeight: z.number().nullable().optional(),
  }),
  description:
    "Displays an image with optional caption. Use for article thumbnails, publisher logos, or brand assets. Props: src (image URL), alt (accessible description), caption (optional text below image), rounded (round corners, default true), maxHeight (px, default 200 in chat context).",
  example: {
    src: "https://example.com/photo.jpg",
    alt: "Article thumbnail",
    caption: "Reuters / Jane Doe",
    rounded: true,
    maxHeight: 200,
  },
} as const;

type ChatImagePropsFromSpec = {
  src?: string;
  alt?: string;
  caption?: string | null;
  rounded?: boolean | null;
  maxHeight?: number | null;
};

/**
 * Adapter for json-render defineRegistry: renders ChatImage from catalog props.
 */
export const ChatImageFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const typed = props as ChatImagePropsFromSpec;
  return (
    <ChatImage
      src={typed.src ?? ""}
      alt={typed.alt ?? ""}
      caption={typed.caption ?? undefined}
      rounded={typed.rounded ?? undefined}
      maxHeight={typed.maxHeight ?? undefined}
    />
  );
};
